import React, { createContext, useContext } from 'react';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { cn } from '../../lib/cn';

type DialogContextValue = {
  open: boolean;
  onOpenChange: ((open: boolean) => void) | undefined;
};

const DialogContext = createContext<DialogContextValue>({ open: false, onOpenChange: undefined });

export function Dialog({
  open = false,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({
  children,
  asChild,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, props);
  }
  return (
    <button type="button" className={cn('cursor-pointer', className)} {...props}>
      {children}
    </button>
  );
}

export function DialogContent({
  children,
  className,
  showCloseButton: _showCloseButton,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { showCloseButton?: boolean }) {
  const { open, onOpenChange } = useContext(DialogContext);
  if (!open) return null;

  return (
    <ViewportOverlay open={open} onClose={() => onOpenChange?.(false)}>
      <div
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'recall-sequence-wizard fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-2rem)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-6 shadow-[var(--sh-3)]',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ViewportOverlay>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-base font-semibold text-[var(--ink-0)]', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-[var(--ink-3)]', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex justify-end gap-2', className)} {...props} />;
}

export function DialogClose({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onOpenChange } = useContext(DialogContext);
  return (
    <button
      type="button"
      className={cn('cursor-pointer', className)}
      onClick={() => onOpenChange?.(false)}
      {...props}
    >
      {children}
    </button>
  );
}
